import React, { FC, useState, useEffect } from "react";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import styles from "pages/References/UsersPage/AddingUser/AddingUser.module.sass";
import { getOrganizationsDirectory } from "toolkit/directories/directories.thunks";
import { addUser, signUpUser } from "toolkit/users/users.thunks";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { ClearError } from "toolkit/users/users.slice";
import { MyInput } from "pages/StepPages/StepTwo/MyInput/MyInput";

interface AddingUserProps {
	close: () => void;
}

export const AddingUser: FC<AddingUserProps> = ({ close }) => {
	const dispatch = useAppDispatch();
	const [name, setName] = useState<string>("");
	const [surname, setSurname] = useState<string>("");
	const [patronymic, setPatronymic] = useState<string>("");
	const [submitError, setSubmitError] = useState<boolean>(false);
	const [email, setEmail] = useState<string>("");
	const [password, setPassword] = useState<string>("");
	const [repeatPassword, setRepeatPassword] = useState<string>("");
	const error = useAppSelector((state) => state.users.error);
	const [repeatedPasswordCorrect, setRepeatedPasswordCorrect] = useState<boolean>(true);
	const [passwordCorrect, setPasswordCorrect] = useState<boolean>(true);
	const [selectedOrganization] = useState<{
		id: number;
		name: string;
	}>({
		id: 0,
		name: "",
	});
	const organizationsDirectory = useAppSelector((state) => state.directories.organizationsDirectory);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [organizationsList, setOrganizationsList] = useState<any[]>([]);
	useEffect(() => {
		if (organizationsDirectory.length) {
			const newOrganizationsDirectory: any[] = [];
			organizationsDirectory.forEach((item) => {
				newOrganizationsDirectory.push({ id: item.id, name: item.shortName });
			});
			setOrganizationsList(newOrganizationsDirectory);
		}
	}, [organizationsDirectory]);

	useEffect(() => {
		if (!organizationsDirectory.length) dispatch(getOrganizationsDirectory());
		if (error.status) dispatch(ClearError());
		//eslint-disable-next-line
	}, []);

	const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		comparePasswords();
		if (
			!name.trim() ||
			!surname.trim() ||
			!patronymic.trim() ||
			!validateEmail() ||
			!validatePassword() ||
			!validateRepeatPassword() ||
			!selectedOrganization.name
		) {
			setSubmitError(true);
		} else {
			setSubmitError(false);
			dispatch(
				signUpUser({
					user: {
						email,
						firstName: name,
						lastName: surname,
						middleName: patronymic,
						organization: selectedOrganization.id,
						password,
					},
					close,
				}),
			);
		}
	};

	const comparePasswords = () => {
		if (password !== repeatPassword) setRepeatedPasswordCorrect(false);
		else setRepeatedPasswordCorrect(true);
	};

	const checkPassword = () => {
		if (validatePassword()) setPasswordCorrect(true);
		else setPasswordCorrect(false);
	};

	useEffect(() => {
		if (repeatedPasswordCorrect) return;
		comparePasswords();
		//eslint-disable-next-line
	}, [password, repeatPassword, repeatedPasswordCorrect]);

	const validateRepeatPassword = (): boolean => {
		const regExp = "^(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$";
		if (!repeatPassword.match(regExp) || password !== repeatPassword) return false;
		return true;
	};

	const validatePassword = (): boolean => {
		const regExp = "^(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$";
		if (!password.match(regExp)) return false;
		return true;
	};

	const validateEmail = (): boolean => {
		const regExp = "^[_A-Za-z0-9-+]+(.[_A-Za-z0-9-]+)*@[A-Za-z0-9-]+(.[A-Za-z0-9]+)*(.[A-Za-z]{2,})$";
		if (!email.match(regExp) || !email.includes(".") || email.includes(" ")) return false;
		return true;
	};

	const emailChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (error.status) dispatch(ClearError());
		setEmail(e.target.value);
	};

	const handleRegister = () => {
		dispatch(addUser({ email, password, name, surname, patronymic }));
		close();
	};

	return (
		<section className={styles.wrp}>
			<h4 className={styles.title}>Добавьте пользователя</h4>
			<form onSubmit={submitHandler} className={styles.form}>
				<div className={styles["form-wrp"]}>
					<MyInput
						className={styles.item}
						name="Фамилия"
						placeholder="Константинопольский"
						value={surname}
						set={(e) => setSurname(e.target.value)}
						required={true}
						condition={submitError && !surname.trim()}
					/>
					<MyInput
						className={styles.item}
						name="Имя"
						placeholder="Константин"
						value={name}
						set={(e) => setName(e.target.value)}
						required={true}
						condition={submitError && !name.trim()}
					/>
					<MyInput
						className={styles.item}
						name="Отчество"
						placeholder="Константинович"
						value={patronymic}
						set={(e) => setPatronymic(e.target.value)}
						required={true}
						condition={submitError && !patronymic.trim()}
					/>
					{/* <MySelect className={styles.item} list={organizationsList} value={selectedOrganization.name} set={(selected) => {setSelectedOrganization(selected)}} name="Организация" placeholder="Выберите организацию" required={true} error={submitError} width="full" /> */}
					<MyInput
						className={styles.item}
						name="Почта"
						placeholder="address@domain.ru"
						value={email}
						set={(e) => emailChangeHandler(e)}
						required={true}
						condition={(submitError && !validateEmail()) || error?.status === 409}
						warning={{ text: error.message, condition: error?.status === 409 }}
					/>
					<MyInput
						className={styles.item}
						name="Пароль"
						placeholder="**********"
						value={password}
						set={(e) => setPassword(e.target.value)}
						description="Пароль должен состоять минимум из 8 символов, должна быть
            минимум одна заглавная буква и не меньше одной цифры"
						type="password"
						required={true}
						condition={(submitError || !passwordCorrect) && !validatePassword()}
						warning={{
							text: "Не соблюдены условия",
							condition: (submitError || !passwordCorrect) && !validatePassword(),
						}}
						blur={checkPassword}
					/>
					<MyInput
						className={styles.item}
						name="Подтверждение пароля"
						placeholder="**********"
						value={repeatPassword}
						set={(e) => setRepeatPassword(e.target.value)}
						type="password"
						required={true}
						condition={(submitError || !repeatedPasswordCorrect) && !validateRepeatPassword()}
						warning={{
							text: "Пароли не совпадают",
							condition: !repeatedPasswordCorrect,
						}}
						blur={comparePasswords}
					/>
				</div>
				<div className={styles.btns}>
					<Button onClick={close} type="button" variant={BUTTON_VARIANTS.OUTLINED} className={styles.button}>
						Отменить
					</Button>
					<Button type="submit" className={styles.button} onClick={handleRegister}>
						Готово
					</Button>
				</div>
			</form>
		</section>
	);
};
